import { useState } from "react";
import "./PricingHeroPlan.css";

const TABS = [
  { id: "all", label: "All Plans", icon: "grid" },
  { id: "hourly", label: "On-Demand Care", icon: "timer" },
  { id: "short", label: "Short-Term Care", icon: "calendar" },
  { id: "long", label: "Ongoing Care", icon: "shield" },
];

const TAB_CONTENT = {
  all: {
    eyebrow: "Flexible plans. Complete control.",
    title: "Care, built around your needs",
    features: [
      {
        tone: "green",
        icon: "rupee",
        title: "Pay for the Care You Choose",
        desc: "Discuss rates and payment arrangements directly with your chosen professional.",
      },
      {
        tone: "blue",
        icon: "lock",
        title: "Understand Applicable Fees",
        desc: "Review platform or transaction fees, where applicable, before proceeding.",
      },
      {
        tone: "violet",
        icon: "swap",
        title: "Flexible Care Arrangements",
        desc: "Discuss schedules, duration and terms directly with your chosen professional.",
      },
    ],
    ctaTitle: "View detailed pricing",
    ctaDesc: "Explore plan features, inclusions and pricing in detail.",
    ctaLabel: "View Pricing Details",
  },
  hourly: {
    eyebrow: "On-demand care",
    title: "Book NCID-verified help by the hour",
    features: [
      {
        tone: "green",
        icon: "rupee",
        title: "Care Pass from ₹100 / booking",
        desc: "Pay the professional directly, subject to applicable platform fees.",
      },
      {
        tone: "blue",
        icon: "lock",
        title: "Instant booking confirmation",
        desc: "Verification assistance and automated reminders.",
      },
      {
        tone: "violet",
        icon: "swap",
        title: "Packs that stretch further",
        desc: "Buy multi-booking packs and save on each session.",
      },
    ],
    ctaTitle: "See on-demand pricing",
    ctaDesc: "Compare hourly booking packs and what’s included.",
    ctaLabel: "View On-Demand Plans",
  },
  short: {
    eyebrow: "Short-term care",
    title: "One-time passes for temporary needs",
    features: [
      {
        tone: "green",
        icon: "rupee",
        title: "Start from ₹500",
        desc: "Mini, weekly, fortnight or monthly care passes.",
      },
      {
        tone: "blue",
        icon: "lock",
        title: "Contact caregivers directly",
        desc: "Unlimited search with replacement assistance.",
      },
      {
        tone: "violet",
        icon: "swap",
        title: "Upgrade by the difference only",
        desc: "Extend your pass anytime without paying twice.",
      },
    ],
    ctaTitle: "See short-term pricing",
    ctaDesc: "Choose the pass length that matches your recovery window.",
    ctaLabel: "View Short-Term Plans",
  },
  long: {
    eyebrow: "Ongoing care",
    title: "Continuous home care, best value",
    features: [
      {
        tone: "green",
        icon: "rupee",
        title: "₹3,000 for 3 months",
        desc: "Equivalent to just ₹1,000 per month.",
      },
      {
        tone: "blue",
        icon: "lock",
        title: "Unlimited marketplace access",
        desc: "Search, contact and hire with priority support.",
      },
      {
        tone: "violet",
        icon: "swap",
        title: "No lock-in or exit fees",
        desc: "Cancel anytime. Simple quarterly renewal.",
      },
    ],
    ctaTitle: "See ongoing care pricing",
    ctaDesc: "Review quarterly inclusions, launch benefits and renewal.",
    ctaLabel: "View Ongoing Plans",
  },
};

function TabIcon({ name }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.85",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  if (name === "grid") {
    return (
      <svg {...common}>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    );
  }
  if (name === "timer") {
    return (
      <svg {...common}>
        <circle cx="12" cy="13" r="8" />
        <path d="M12 9v4l2.5 1.5" />
        <path d="M9 2h6" />
      </svg>
    );
  }
  if (name === "calendar") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function FeatureIcon({ name }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  if (name === "rupee") {
    return (
      <svg {...common}>
        <path d="M7 7h10" />
        <path d="M7 11h10" />
        <path d="M9.2 7c3.2 0 5.3 1.7 5.3 4.1S13 15.2 9.2 15.2" />
        <path d="M9.2 15.2 16.5 21" />
      </svg>
    );
  }
  if (name === "lock") {
    return (
      <svg {...common}>
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V8a4 4 0 018 0v3" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M16 3h5v5" />
      <path d="M8 21H3v-5" />
      <path d="M21 3l-7.5 7.5M3 21l7.5-7.5" />
    </svg>
  );
}

function ClipboardArt() {
  return (
    <svg className="pricing-hero__art" viewBox="0 0 200 240" fill="none" aria-hidden="true">
      {/* Clipboard body */}
      <rect x="42" y="36" width="116" height="168" rx="20" fill="#fff" stroke="#C7DBFF" strokeWidth="3.5" />
      {/* Clip header */}
      <rect x="68" y="18" width="64" height="34" rx="12" fill="#1E5BFF" />
      <rect x="84" y="28" width="32" height="12" rx="6" fill="#93C5FD" />
      {/* Top lines */}
      <rect x="66" y="68" width="68" height="11" rx="5.5" fill="#BFDBFE" />
      <rect x="66" y="90" width="52" height="9" rx="4.5" fill="#DBEAFE" />
      {/* Checklist rows — green check circles */}
      <circle cx="74" cy="124" r="12" fill="#22C55E" />
      <path d="M68 124.2 72.2 128.4 81 119.6" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="96" y="119" width="42" height="10" rx="5" fill="#E2E8F0" />

      <circle cx="74" cy="156" r="12" fill="#22C55E" />
      <path d="M68 156.2 72.2 160.4 81 151.6" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="96" y="151" width="36" height="10" rx="5" fill="#E2E8F0" />

      <circle cx="74" cy="188" r="12" fill="#22C55E" />
      <path d="M68 188.2 72.2 192.4 81 183.6" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="96" y="183" width="40" height="10" rx="5" fill="#E2E8F0" />

      {/* Blue shield badge */}
      <circle cx="148" cy="186" r="30" fill="#1E5BFF" />
      <path
        d="M148 166c-12 0-22 6.5-22 15v13c0 9 10 16.5 22 20 12-3.5 22-11 22-20v-13c0-8.5-10-15-22-15z"
        fill="#fff"
      />
      <path d="M139.5 184.5 146 191l12.5-12.5" stroke="#1E5BFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
      <path
        d="M20.59 13.41 13.42 20.58a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="7.5" cy="7.5" r="1.35" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function PricingHeroPlan({ standalone = false, onViewDetails, onGetStarted }) {
  const [activeTab, setActiveTab] = useState("all");
  const HeadingTag = standalone ? "h1" : "h2";
  const content = TAB_CONTENT[activeTab] || TAB_CONTENT.all;

  function handleViewDetails() {
    if (typeof onViewDetails === "function") {
      onViewDetails(activeTab === "all" ? null : activeTab);
      return;
    }
    if (typeof onGetStarted === "function") {
      onGetStarted();
    }
  }

  return (
    <section className="pricing-hero" aria-labelledby="pricing-hero-heading">
      <div className="pricing-hero__head">
        <p className="pricing-hero__eyebrow">PRICING &amp; PLANS</p>
        <HeadingTag id="pricing-hero-heading">Simple, Transparent Pricing for Your Care Needs</HeadingTag>
        <p className="pricing-hero__lead">
          Explore available care options, review listed rates and understand applicable fees before hiring.
        </p>
      </div>

      <div className="pricing-hero__tabs" role="tablist" aria-label="Pricing categories">
        {TABS.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`pricing-hero__tab${selected ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <TabIcon name={tab.icon} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="pricing-hero__panel" role="tabpanel">
        <div className="pricing-hero__visual" aria-hidden="true">
          <div className="pricing-hero__visual-frame">
            <ClipboardArt />
          </div>
        </div>

        <div className="pricing-hero__copy">
          <p className="pricing-hero__kicker">{content.eyebrow}</p>
          <h3 className="pricing-hero__panel-title">{content.title}</h3>
          <ul className="pricing-hero__features">
            {content.features.map((item) => (
              <li key={item.title} className="pricing-hero__feature">
                <span className={`pricing-hero__feature-icon pricing-hero__feature-icon--${item.tone}`}>
                  <FeatureIcon name={item.icon} />
                </span>
                <div className="pricing-hero__feature-copy">
                  <strong>{item.title}</strong>
                  <span>{item.desc}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <aside className="pricing-hero__aside">
          <span className="pricing-hero__aside-icon">
            <TagIcon />
          </span>
          <strong className="pricing-hero__aside-title">{content.ctaTitle}</strong>
          <p className="pricing-hero__aside-desc">{content.ctaDesc}</p>
          <button type="button" className="pricing-hero__cta" onClick={handleViewDetails}>
            <span>{content.ctaLabel}</span>
            <span aria-hidden="true">→</span>
          </button>
        </aside>
      </div>
    </section>
  );
}
